import { FunctionComponent, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './transformRegularSlot.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { CalendarMonth, LockOutlined } from '@mui/icons-material'
import { NewOccasionalSlot, OccasionalSlotsPlaces, RegularSlot } from '@/types/Slots'
import moment from 'moment-timezone'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import ScheduleOccasionalSlot from '../../occasionalSlot/scheduleOccasionalSlot/scheduleOccasionalSlotDialog'
import { useMutation } from '@apollo/client'
import { CREATE_OCCASIONAL_SLOT } from '@/modules/API/slot/occasionalSlotHandler'
import { Section } from '@/types/Section'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import { format } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog'

interface TransformRegularSlotProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    regularSlots: RegularSlot[]
    date: Date | null
    confirmAllRegularSlots: () => void
    dateList: string[]
    places: OccasionalSlotsPlaces[]
    isRpeOrLaep: boolean
}

const TransformRegularSlot: FunctionComponent<TransformRegularSlotProps> = ({
    open,
    daycare,
    regularSlots,
    date,
    handleClose,
    confirmAllRegularSlots,
    dateList,
    places,
    isRpeOrLaep
}: TransformRegularSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [start, setStart] = useState<string>(moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm'))
    const [end, setEnd] = useState<string>(moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm'))

    const [place, setPlace] = useState<string>(regularSlots[0].placeId ? regularSlots[0].placeId.toString() : '')

    const [isScheduleOccasionalSlotDialogOpen, setScheduleOccasionalSlotDialogOpen] = useState(false)
    const [scheduleDate, setScheduleDate] = useState<string>('')

    const [selectSection, setSelectSection] = useState<string>(
        regularSlots.length > 0 && regularSlots[0].sectionId
            ? regularSlots[0].sectionId.toString()
            : daycare.sections && daycare.sections.length > 0
              ? daycare.sections[0].id.toString()
              : ''
    )

    const selectOptionsSection: SelectOptionsDeprecated = [
        {
            options: daycare.sections
                ? daycare?.sections.map((section: Section): SelectOptionDeprecated => {
                      return {
                          name: section.name,
                          value: section.id.toString()
                      }
                  })
                : []
        }
    ]

    const selectOptionsPlace: SelectOptionsDeprecated = [
        {
            options: [
                ...places.map((place: OccasionalSlotsPlaces): SelectOptionDeprecated => {
                    return {
                        name: place.name.valueOf(),
                        value: place.id.toString()
                    }
                }),
                {
                    name: t('noPlaces'),
                    value: ''
                }
            ]
        }
    ]

    function createNewOccasionalSlot(): NewOccasionalSlot[] {
        const dates: string[] =
            dateList && dateList.length > 0 ? dateList : [moment(date, 'DD-MM-YYYY').format('DD-MM-YYYY')]

        return dates.flatMap((d: string) => {
            const baseSlot: NewOccasionalSlot = {
                date: d,
                start: start,
                end: end,
                childId: undefined,
                sectionId: parseInt(selectSection),
                childrenPriority: [],
                groupPriority: [],
                scheduled: isScheduleOccasionalSlotDialogOpen
                    ? scheduleDate === ''
                        ? moment(`${d} ${end}`, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY HH:mm')
                        : scheduleDate
                    : undefined,
                externalIcon: undefined,
                expiry: undefined,
                internalNote:
                    t('absenceOf') +
                    ' ' +
                    regularSlots
                        .map((regularRequest: RegularSlot) => {
                            return (
                                regularRequest.childName +
                                (regularRequest.childComplement ? ' - ' + regularRequest.childComplement : '')
                            )
                        })
                        .join(', '),
                externalNote: undefined,
                placeId: place === '' ? undefined : Number(place)
            }

            return Array.from({ length: regularSlots.length }, () => ({ ...baseSlot }))
        })
    }

    const [createOccasionalSlot, { loading: loadingCreate }] = useMutation(CREATE_OCCASIONAL_SLOT, {
        variables: {
            daycare_id: daycare.id,
            occasionalSlots: createNewOccasionalSlot()
        },
        onCompleted: () => {
            confirmAllRegularSlots()
            handleClose()

            showNotificationSuccess(t('absenceTransformed'))
        },
        onError: () => {
            showNotificationError(t('errorTransformedAbsence'))
        }
    })

    const { dialog: ScheduleOccasionalSlotDialog, openDialog: openScheduleOccasionalSlotDialog } = useDialog({
        dialogTitle: t('scheduled'),
        Component: ScheduleOccasionalSlot,
        props: (handleClose) => ({
            handleClose: () => {
                handleClose()
                setScheduleOccasionalSlotDialogOpen(false)
            },
            scheduleDate,
            setScheduleDate,
            createOccasionalSlot,
            maxDate: moment(date, 'DD-MM-YYYY').format('DD-MM-YYYY')
        }),
        hideDialogClose: true
    })

    function handleOpenScheduleOccasionalSlotDialog() {
        setScheduleOccasionalSlotDialogOpen(true)
        openScheduleOccasionalSlotDialog()
    }

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader title={t('transformAbsence')} />
                <DialogBody className={styles.DialogBody}>
                    <div className={styles.Container}>
                        {dateList.length > 0 ? (
                            <Autocomplete
                                multiple
                                options={dateList}
                                value={dateList}
                                renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                                    value.map((option: string, index: number) => {
                                        const { key, ...tagProps } = getTagProps({ index })

                                        return <Chip key={key} label={option} {...tagProps} />
                                    })
                                }
                                renderInput={(params: AutocompleteRenderInputParams) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        rows={1}
                                        multiline={true}
                                        label={t('dateList')}
                                    />
                                )}
                                className={styles.AutocompleteDaycare}
                                readOnly={true}
                            />
                        ) : (
                            <>
                                <CalendarMonth className={styles.Icon} />
                                <div className={styles.TextContainer}>
                                    {date && (
                                        <p className={styles.FirstText}>
                                            {format(date, 'EEEE dd MMMM yyyy', {
                                                locale: getLocalFromLanguage()
                                            })}
                                        </p>
                                    )}
                                    <p className={styles.SecondText}>
                                        {t('from') +
                                            ' ' +
                                            moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                            ' ' +
                                            t('to').toLowerCase() +
                                            ' ' +
                                            moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.Input}>
                        <TimePicker value={start} setValue={setStart} className={styles.UserInput} />
                        <TimePicker value={end} setValue={setEnd} className={styles.UserInput} />
                    </div>
                    <div className={styles.Input}>
                        <SelectUnique
                            selectOptions={selectOptionsSection}
                            selectValue={selectSection}
                            setSelectValue={setSelectSection}
                            formClassName={styles.UserInput}
                        />
                    </div>
                    {isRpeOrLaep && (
                        <div className={styles.Input}>
                            <SelectUnique
                                selectOptions={selectOptionsPlace}
                                selectValue={place}
                                setSelectValue={setPlace}
                                formClassName={styles.UserInput}
                                label={t('places')}
                            />
                        </div>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        <ButtonTextIcon
                            type={'custom'}
                            customIcon={<LockOutlined />}
                            customText={t('scheduled')}
                            className={styles.Button}
                            onClick={handleOpenScheduleOccasionalSlotDialog}
                        />
                        <ButtonTextIcon
                            type={'add'}
                            onClick={createOccasionalSlot}
                            className={styles.Button}
                            disabled={loadingCreate}
                        />
                    </div>
                </DialogBody>
            </Dialog>

            {ScheduleOccasionalSlotDialog}
        </>
    )
}

export default TransformRegularSlot
